import { useLazyGetCompanyOfficesQuery, useLazyGetOfficeCoordinatesQuery } from "features/tasks/api"
import { useLazyGetProjectsByCityQuery } from "features/shared/api"
import { getPlusCodeTooltip, isCompanyOffice, isRequiredSubofSubTask, isSiteOffice, isWalkInOrMeeting } from "../utilis"
import { Icon, notification, SelectInput, TextInput } from "components/common"
import { IconText } from "components/custom"
import { Form } from "antd"
import { useSelector } from "react-redux"
import { useEffect, useMemo, useCallback } from "react"
import { OpenLocationCode } from "open-location-code"
import { t } from "i18next"
import theme from "utils/themeVars"

const fieldMap = {
  walk_in: "companyOffice",
  company_office: "companyOffice",
  company_office_meeting_attempted: "companyOffice",
  outdoor: "plusCode",
  outdoor_meeting_attempted: "plusCode",
  site_office: "visitedProjects",
  site_office_meeting_attempted: "visitedProjects",
}

const SubTaskAdditionalFields = ({ leadDetailInfo, rules, form }) => {
  const userInfo = useSelector(state => state?.app?.user.info)
  const regionId = userInfo?.Region?.id?.toString()

  const task = Form.useWatch("task", form)
  const subTask = Form.useWatch("subTask", form)
  const subOfSubTask = Form.useWatch("subofSubTask", form)
  const plusCode = Form.useWatch("plusCode", form)

  const [getCompanyOffices, { data: companyOffice, isFetching: isFetchingCompanyOffices }] =
    useLazyGetCompanyOfficesQuery()
  const [getProjectsByCity, { data: projectsByCity, isFetching: isFetchingProjects }] = useLazyGetProjectsByCityQuery()
  const [getCoordinates] = useLazyGetOfficeCoordinatesQuery()

  useEffect(() => {
    if (!leadDetailInfo || !regionId) return

    if (leadDetailInfo.isLocationCompanyOffices) getCompanyOffices({ cityIds: regionId }, true)
    if (leadDetailInfo.isLocationSiteProjects) getProjectsByCity(regionId, true)
  }, [leadDetailInfo, regionId])

  useEffect(() => {
    if (!regionId) return
    if (isWalkInOrMeeting(subTask) || isCompanyOffice(subTask) || isCompanyOffice(subOfSubTask)) {
      getCompanyOffices({ cityIds: regionId }, true)
    }
    if (isSiteOffice(subOfSubTask)) {
      getProjectsByCity(regionId, true)
    }
  }, [subTask, subOfSubTask, regionId])

  const renderAdditionalField = useMemo(() => {
    if (!isRequiredSubofSubTask(subTask)) return null
    const key = subOfSubTask || subTask
    return fieldMap[key] || null
  }, [subTask, subOfSubTask])

  const handleLocateMe = () => {
    if (!navigator?.geolocation) {
      return notification.error({ message: t("Geolocation not supported by your browser") })
    }

    navigator.geolocation.getCurrentPosition(
      position => {
        const { latitude, longitude } = position.coords
        const code = new OpenLocationCode().encode(latitude, longitude)
        form.setFieldsValue({ plusCode: code })
      },
      error => notification.error({ message: error?.message }),
      { enableHighAccuracy: true, maximumAge: 0 }
    )
  }

  const renderCompanyOffice = (
    <Form.Item name="companyOffice" label={t("Company Office")} required rules={rules}>
      <SelectInput
        placeholder={t("Select company office")}
        options={companyOffice}
        inputLoading={isFetchingCompanyOffices}
        onChange={value => form.setFieldsValue({ companyOffice: value })}
        defaultValue={companyOffice?.length === 1 ? companyOffice[0]?.key : null}
      />
    </Form.Item>
  )

  const renderVisitedProjects = (
    <Form.Item name="visitedProject" label={t("Visited Project")} required rules={rules}>
      <SelectInput
        placeholder={t("Select visited project")}
        options={projectsByCity}
        inputLoading={isFetchingProjects}
        onChange={value => form.setFieldsValue({ visitedProject: value })}
      />
    </Form.Item>
  )

  const renderPlusCode = (
    <Form.Item
      name="plusCode"
      label={
        <>
          {t("Plus Code")}
          <Icon
            icon="BsInfo"
            color="#707070"
            size="1rem"
            style={{
              backgroundColor: "#E6E6E6",
              borderRadius: "1rem",
              marginLeft: 6,
              verticalAlign: "middle",
              "--icon-color": "#707070",
            }}
            tooltipText={getPlusCodeTooltip()}
            overlayInnerStyle={{ width: 360 }}
          />
        </>
      }
      required
      rules={rules.concat({
        validator: async (_, value) => {
          if (value) {
            const response = await getCoordinates(value)
            if (response?.error) {
              return Promise.reject(response?.error?.message)
            }
          }
          return Promise.resolve()
        },
      })}
    >
      <TextInput
        placeholder={t("Enter plus code")}
        allowClear
        onChange={() => form.validateFields(["plusCode"])}
        customSuffix={
          !plusCode && (
            <IconText
              icon="BiCurrentLocation"
              iconProps={{ color: theme["primary-color"], size: "1rem" }}
              title={t("Locate Me")}
              textType="secondary"
              titleProps={{
                className: "pointer",
                onClick: handleLocateMe,
              }}
            />
          )
        }
      />
    </Form.Item>
  )

  switch (renderAdditionalField) {
    case "companyOffice":
      return renderCompanyOffice
    case "visitedProjects":
      return renderVisitedProjects
    case "plusCode":
      return renderPlusCode
    default:
      return null
  }
}

export default SubTaskAdditionalFields
